import React from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export default function BoundarySection({
  boundaryType,
  handleBoundaryTypeChange,
  site,
  handleCoordinatesChange,
  handleInputChange,
  addCoordinate,
  removeCoordinate,
  errors,
  touched,
  handleCoordinateBlur,
  handleBlur,
  isMobile,
  setErrors,
  setTouched,
}) {
  const { t } = useTranslation();
  const lastCoordinateIndex = Math.max(0, site.sitCoordinates.length - 1);

  // Handle adding new coordinate (optional, can be used for button-based addition)
  const handleAddCoordinate = () => {
    // Check if current coordinates are empty
    const lastIndex = site.sitCoordinates.length - 1;
    const lastCoordinate = site.sitCoordinates[lastIndex];

    // If there are no coordinates or the last coordinate has both values, add new one
    if (
      site.sitCoordinates.length === 0 ||
      (lastCoordinate && lastCoordinate.longitude && lastCoordinate.latitude)
    ) {
      addCoordinate();
    } else {
      // Show error for empty coordinates
      const newErrors = [...(errors.coordinates || [])];
      newErrors[lastIndex] =
        "Please enter both longitude and latitude before adding new coordinates";
      setErrors((prev) => ({
        ...prev,
        coordinates: newErrors,
      }));

      // Mark fields as touched
      setTouched((prev) => ({
        ...prev,
        [`coordinate_${lastIndex}_longitude`]: true,
        [`coordinate_${lastIndex}_latitude`]: true,
      }));
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "8px",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            minHeight: "44px",
            borderRadius: "8px",
            gap: { xs: "8px", md: "16px" },
            padding: "8px 12px",
            background: "#E8EAEE",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontStyle: "medium",
              fontSize: "16px",
              color: "#647490",
              lineHeight: "1",
            }}
          >
            {t("Boundary Type")}:
          </Typography>

          <RadioGroup
            row
            name="boundaryType"
            value={boundaryType}
            onChange={(e) => handleBoundaryTypeChange(e.target.value)}
            sx={{
              m: 0,
              alignItems: "center",
            }}
          >
            <FormControlLabel
              sx={{
                marginTop: 0,
                "& .MuiFormControlLabel-label": {
                  color: boundaryType === "circular" ? "#20A8E0" : "inherit",
                  fontSize: "24px",
                  fontWeight: 500,
                  fontStyle: "medium",
                },
              }}
              value="circular"
              control={
                <Radio
                  sx={{
                    color: "#95A0B3",
                    "&.Mui-checked": {
                      color: "#20A8E0",
                    },
                    padding: "0 8px",
                  }}
                />
              }
              label={t("Circular")}
            />
            <FormControlLabel
              sx={{
                marginTop: 0,
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  fontStyle: "medium",
                  color: boundaryType === "custom" ? "#20A8E0" : "inherit",
                  fontSize: "24px",
                },
              }}
              value="custom"
              control={
                <Radio
                  sx={{
                    color: "#95A0B3",
                    "&.Mui-checked": {
                      color: "#20A8E0",
                    },
                    padding: "0 8px",
                  }}
                />
              }
              label={t("Custom")}
            />
          </RadioGroup>
        </Box>

        {boundaryType === "circular" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "20px",
                  color: "#1F242E",
                  fontFamily: "Roboto",
                }}
              >
                {t("Central Coordinates")}{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                {t("Click on the map to set coordinates")}
              </Typography>
              <TextField
                fullWidth
                required
                sx={{
                  width: "100%",
                  height: "55px",
                  borderRadius: "16px",
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    border:
                      touched[`coordinate_0_longitude`] &&
                      errors.coordinates?.[0]
                        ? "2px solid #d32f2f"
                        : "2px solid #95A0B3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                type="number"
                // size="small"
                placeholder={t("Longitude (e.g.77.5946)")}
                value={site.sitCoordinates[0]?.longitude || ""}
                onChange={(e) =>
                  handleCoordinatesChange(0, "longitude", e.target.value)
                }
                onBlur={() => handleCoordinateBlur(0, "longitude")}
                error={
                  touched[`coordinate_0_longitude`] &&
                  Boolean(errors.coordinates?.[0])
                }
                inputProps={{ step: "any", min: -180, max: 180 }}
              />
              {touched[`coordinate_0_longitude`] && errors.coordinates?.[0] && (
                <FormHelperText error>{errors.coordinates[0]}</FormHelperText>
              )}

              <TextField
                fullWidth
                required
                sx={{
                  width: "100%",
                  height: "55px",
                  borderRadius: "16px",
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    border:
                      touched[`coordinate_0_latitude`] &&
                      errors.coordinates?.[0]
                        ? "2px solid #d32f2f"
                        : "2px solid #95A0B3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                type="number"
                // size="small"
                placeholder={t("Latitude (e.g.12.9716)")}
                value={site.sitCoordinates[0]?.latitude || ""}
                onChange={(e) =>
                  handleCoordinatesChange(0, "latitude", e.target.value)
                }
                onBlur={() => handleCoordinateBlur(0, "latitude")}
                error={
                  touched[`coordinate_0_latitude`] &&
                  Boolean(errors.coordinates?.[0])
                }
                inputProps={{ step: "any", min: -90, max: 90 }}
              />
              {touched[`coordinate_0_latitude`] && errors.coordinates?.[0] && (
                <FormHelperText error>{errors.coordinates[0]}</FormHelperText>
              )}
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontSize: "16px" }}>
                {t("Radius (Kilometre)")}{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                required
                sx={{
                  width: "100%",
                  height: "55px",
                  borderRadius: "16px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    border:
                      touched.radius && errors.radius
                        ? "2px solid #d32f2f"
                        : "2px solid #95A0B3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                type="number"
                // size="small"
                name="radius"
                placeholder={t("Type Radius (e.g.3)")}
                value={site.radius || ""}
                onChange={handleInputChange}
                onBlur={() => handleBlur("radius")}
                error={touched.radius && Boolean(errors.radius)}
                helperText={touched.radius && errors.radius}
                inputProps={{ step: "any", min: 0 }}
              />
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontSize: "20px", color: "#1F242E" }}
            >
              Boundary Coordinates (Min 3 Points){" "}
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
              {t("Click on the map or enter coordinates to add points")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                required
                sx={{
                  height: "55px",
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    border:
                      touched[`coordinate_${lastCoordinateIndex}_longitude`] &&
                      errors.coordinates?.[lastCoordinateIndex]
                        ? "2px solid #d32f2f"
                        : "2px solid #95A0B3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                type="number"
                // size="small"
                placeholder={t("Longitude (e.g.77.5946)")}
                value={
                  site.sitCoordinates[lastCoordinateIndex]?.longitude || ""
                }
                onChange={(e) => {
                  const index =
                    site.sitCoordinates.length > 0
                      ? site.sitCoordinates.length - 1
                      : 0;
                  if (site.sitCoordinates.length === 0) {
                    handleCoordinatesChange(0, "longitude", e.target.value);
                  } else {
                    handleCoordinatesChange(index, "longitude", e.target.value);
                  }
                }}
                onBlur={() =>
                  handleCoordinateBlur(lastCoordinateIndex, "longitude")
                }
                error={
                  touched[`coordinate_${lastCoordinateIndex}_longitude`] &&
                  Boolean(errors.coordinates?.[lastCoordinateIndex])
                }
                inputProps={{ step: "any", min: -180, max: 180 }}
              />
              {touched[`coordinate_${lastCoordinateIndex}_longitude`] &&
                errors.coordinates?.[lastCoordinateIndex] && (
                  <FormHelperText error>
                    {errors.coordinates[lastCoordinateIndex]}
                  </FormHelperText>
                )}
              <TextField
                fullWidth
                required
                sx={{
                  mb: 1,
                  height: "55px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    border:
                      touched[`coordinate_${lastCoordinateIndex}_latitude`] &&
                      errors.coordinates?.[lastCoordinateIndex]
                        ? "2px solid #d32f2f"
                        : "2px solid #95A0B3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                type="number"
                // size="small"
                placeholder={t("Latitude (e.g.12.9716)")}
                value={
                  site.sitCoordinates[lastCoordinateIndex]?.latitude || ""
                }
                onChange={(e) => {
                  const index =
                    site.sitCoordinates.length > 0
                      ? site.sitCoordinates.length - 1
                      : 0;
                  if (site.sitCoordinates.length === 0) {
                    handleCoordinatesChange(0, "latitude", e.target.value);
                  } else {
                    handleCoordinatesChange(index, "latitude", e.target.value);
                  }
                }}
                onBlur={() =>
                  handleCoordinateBlur(lastCoordinateIndex, "latitude")
                }
                error={
                  touched[`coordinate_${lastCoordinateIndex}_latitude`] &&
                  Boolean(errors.coordinates?.[lastCoordinateIndex])
                }
                inputProps={{ step: "any", min: -90, max: 90 }}
              />
              {touched[`coordinate_${lastCoordinateIndex}_latitude`] &&
                errors.coordinates?.[lastCoordinateIndex] && (
                  <FormHelperText error>
                    {errors.coordinates[lastCoordinateIndex]}
                  </FormHelperText>
                )}
              <Button
                variant="contained"
                onClick={handleAddCoordinate}
                sx={{
                  mt: 1,
                  padding: "10px 20px",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  height: "43px",
                  borderRadius: "8px",
                  fontSize: "20px",
                  bgcolor: "#20A8E0",
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#20A8E0",
                  },
                }}
              >
                + Add Coordinates
              </Button>
            </Box>
            <Box
              sx={{
                height: "30vh",
                mt: 2,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {site.sitCoordinates
                .filter((coord) => coord.longitude || coord.latitude)
                .map((coord, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // justifyContent: "space-between",
                      justifyContent: "flex-start",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography>{index + 1}.</Typography>
                    <Typography sx={{ fontSize: "16px" }}>
                      Longitude:{" "}
                      <span style={{ fontWeight: 600 }}>
                        {coord.longitude || ""}
                      </span>
                      &nbsp; Latitude:{" "}
                      <span style={{ fontWeight: 600 }}>
                        {coord.latitude || ""}
                      </span>
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeCoordinate(index)}
                      sx={{
                        width: "14px",
                        height: "14px",
                        fontSize: "12px",
                        p: 0,
                        minWidth: "unset",
                        borderRadius: "50%",
                        textTransform: "none",
                        border: "2px solid #EE3B2B",
                      }}
                    >
                      x
                    </Button>
                  </Box>
                ))}
            </Box>
            {touched[`coordinate_${lastCoordinateIndex}_latitude`] &&
              errors.coordinates &&
              errors.coordinates.some((error) => error) && (
                <FormHelperText error>
                  Please check all coordinate inputs
                </FormHelperText>
              )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
